import { getDomainUserId } from 'analytics/snowplow/helpers/domainUserId'
import { getLastKnownMemberId } from 'analytics/snowplow/helpers/lastKnownMemberId'
import { getStitchedSeedId, stitchedSeedIdCookieName } from 'analytics/snowplow/helpers/stitchedSeedId'
import { getSeedIdFromApi, SEED_ID_REQUEST_TIMEOUT_MS } from 'api/lab'
import { setCookie } from 'lib/web/cookieUtils'
import { DurationSeconds } from 'lib/datetime/durationInSeconds'

function getDomainUserIdFromState(state: App.State): string | undefined {
  return state.auth.domainUserId ?? getDomainUserId()
}

function getMemberIdForStitchedSeedLookup(state: App.State): string | undefined {
  return state.auth.account?.memberId ?? getLastKnownMemberId()
}

/**
 * Lab decide distinct ID: stitched seed when available, otherwise the domain user ID.
 */
export function getLabDistinctId(state: App.State): string {
  return getLabDistinctIdForRequest(state) ?? 'anonymous'
}

export function getLabDistinctIdForRequest(state: App.State): string | undefined {
  const domainUserId = getDomainUserIdFromState(state)
  const resolvedTrackingId = state.auth.resolvedTrackingId
  const memberId = getMemberIdForStitchedSeedLookup(state)
  const stitchedSeedId = memberId ? getStitchedSeedId(memberId) : undefined

  if (resolvedTrackingId) {
    return resolvedTrackingId
  }

  if (stitchedSeedId) {
    return stitchedSeedId
  }

  return domainUserId
}

/**
 * Uses the LERE decision's cached identity without fetching a missing stitched seed.
 * The cookie takes precedence because Redux can retain a pre-login tracking ID until
 * the post-login identity update completes.
 */
export function getCachedLereLabDistinctId(state: App.State): string | undefined {
  const memberId = getMemberIdForStitchedSeedLookup(state)
  const stitchedSeedId = memberId ? getStitchedSeedId(memberId) : undefined

  return stitchedSeedId ?? state.auth.resolvedTrackingId ?? getDomainUserIdFromState(state)
}

/**
 * True when we can run member-linked LERE Lab decide:
 * logged-in member, lastKnownMemberId, or SSR stitched resolvedTrackingId.
 */
export function hasStitchedLabDistinctId(state: App.State): boolean {
  if (getMemberIdForStitchedSeedLookup(state)) {
    return true
  }

  return hasResolvedStitchedLabDistinctId(state)
}

/** True when Redux has resolved the tracking identity away from the anonymous domain ID. */
export function hasResolvedStitchedLabDistinctId(state: App.State): boolean {
  const domainUserId = getDomainUserIdFromState(state)
  const resolvedTrackingId = state.auth.resolvedTrackingId
  return !!resolvedTrackingId && !!domainUserId && resolvedTrackingId !== domainUserId
}

/**
 * Start the stitched seed-id fetch as soon as login succeeds, before other
 * post-login work. `updateResolvedTrackingId` joins this in-flight request.
 */
export function prefetchStitchedSeedId(memberId?: string) {
  if (!memberId || getStitchedSeedId(memberId)) return
  getSeedIdFromApi(memberId, { timeout: SEED_ID_REQUEST_TIMEOUT_MS }).catch(() => undefined)
}

/**
 * LERE Lab distinct ID: member stitched seed (fetch + cookie if needed).
 * Same id logged in or out via lastKnownMemberId.
 */
export async function resolveLereLabDistinctId(state: App.State): Promise<string | undefined> {
  const memberId = getMemberIdForStitchedSeedLookup(state)
  if (!memberId) {
    return getCachedLereLabDistinctId(state)
  }

  let stitchedSeedId = getStitchedSeedId(memberId)
  if (!stitchedSeedId) {
    try {
      stitchedSeedId = await getSeedIdFromApi(memberId)
      if (stitchedSeedId && !getStitchedSeedId(memberId)) {
        setCookie(
          stitchedSeedIdCookieName(memberId),
          stitchedSeedId,
          DurationSeconds.ONE_YEAR,
          '/',
          'Lax',
        )
      }
    } catch {
      stitchedSeedId = undefined
    }
  }

  return stitchedSeedId ?? getCachedLereLabDistinctId(state)
}
